import { useState, useCallback } from 'react';
import {
  Button, Typography, Tag, Space, Descriptions, message,
  Modal, Flex, Input, InputNumber, Select, Form,
} from 'antd';
import {
  PlusOutlined, CheckOutlined, DollarOutlined, RollbackOutlined,
} from '@ant-design/icons';
import type { ColumnDef } from '@shared/types';
import { DISPLAY_DATE_FORMAT, BASE_CURRENCY_CODE } from '@shared/constants';
import { DataTable, DetailDrawer } from '../../components/shared';
import type { FetchParams } from '../../components/shared';
import {
  fetchReturns, getReturn, createReturn, approveReturn, settleReturn,
  type PurchReturn,
} from '../../api/purchasing';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// ── Status config ──

const RETURN_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'processing' },
  approved: { label: 'Approved', color: 'success' },
  settled: { label: 'Settled', color: 'default' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

// ── Column Definitions ──

const columns: ColumnDef<PurchReturn>[] = [
  { key: 'returnCode', label: 'Return #', dataType: 'string', width: 130 },
  {
    key: 'status', label: 'Status', dataType: 'enum', width: 120,
    options: Object.entries(RETURN_STATUS_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label, color: cfg.color })),
    render: (v) => {
      const cfg = RETURN_STATUS_CONFIG[v as string] || { label: v, color: 'default' };
      return <Tag color={cfg.color}>{cfg.label}</Tag>;
    },
  },
  {
    key: 'grnLine', label: 'GRN #', dataType: 'string', width: 130,
    render: (v) => {
      const grnLine = v as PurchReturn['grnLine'];
      return grnLine?.grnHeader?.grnCode || '—';
    },
  },
  { key: 'returnQty', label: 'Return Qty', dataType: 'number', width: 100 },
  { key: 'returnValue', label: 'Return Value', dataType: 'currency', width: 140 },
  { key: 'returnReason', label: 'Reason', dataType: 'string', width: 180 },
  {
    key: 'settlementType', label: 'Settlement', dataType: 'enum', width: 110,
    options: [
      { value: 'credit', label: 'Credit', color: 'blue' },
      { value: 'refund', label: 'Refund', color: 'green' },
    ],
    render: (v) => {
      if (!v) return <Text type="secondary">—</Text>;
      return <Tag color={v === 'credit' ? 'blue' : 'green'}>{v === 'credit' ? 'Credit' : 'Refund'}</Tag>;
    },
  },
  { key: 'returnDate', label: 'Return Date', dataType: 'date', width: 130 },
];

// ── Create Return Modal ──

function CreateReturnModal({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await createReturn({
        grnLineId: values.grnLineId,
        returnQty: values.returnQty,
        returnReason: values.returnReason,
        reasonCode: values.reasonCode || undefined,
      });
      message.success('Return created');
      form.resetFields();
      onClose();
      onCreated();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to create return';
      message.error(msg || 'Failed to create return');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title="Create Return"
      width={520}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave} icon={<RollbackOutlined />}>
            Create Return
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Form.Item
          name="grnLineId"
          label="GRN Line ID"
          rules={[{ required: true, message: 'GRN Line ID is required' }]}
        >
          <InputNumber style={{ width: '100%' }} min={1} placeholder="Enter GRN line ID" />
        </Form.Item>
        <Form.Item
          name="returnQty"
          label="Return Quantity"
          rules={[{ required: true, message: 'Quantity is required' }]}
        >
          <InputNumber style={{ width: '100%' }} min={0.01} precision={2} placeholder="Qty to return" />
        </Form.Item>
        <Form.Item
          name="returnReason"
          label="Return Reason"
          rules={[{ required: true, message: 'Reason is required' }]}
        >
          <Input.TextArea rows={2} placeholder="Why are you returning this?" maxLength={200} showCount />
        </Form.Item>
        <Form.Item name="reasonCode" label="Reason Code">
          <Select
            placeholder="Select reason code"
            allowClear
            options={[
              { label: 'Defective', value: 'defective' },
              { label: 'Wrong Item', value: 'wrong_item' },
              { label: 'Damaged', value: 'damaged' },
              { label: 'Over Delivery', value: 'over_delivery' },
              { label: 'Quality Fail', value: 'quality_fail' },
              { label: 'Other', value: 'other' },
            ]}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Settle Return Modal ──

function SettleReturnModal({
  open, returnRecord, onClose, onSettled,
}: {
  open: boolean;
  returnRecord: PurchReturn | null;
  onClose: () => void;
  onSettled: () => void;
}) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!returnRecord) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await settleReturn(returnRecord.id, {
        settlementType: values.settlementType,
        creditNoteRef: values.creditNoteRef || undefined,
        refundRef: values.refundRef || undefined,
        refundAmount: values.refundAmount,
      });
      message.success('Return settled');
      form.resetFields();
      onClose();
      onSettled();
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'errorFields' in err) return;
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to settle';
      message.error(msg || 'Failed to settle');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Settle Return ${returnRecord?.returnCode || ''}`}
      width={480}
      footer={
        <Space>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={handleSave} icon={<DollarOutlined />}>
            Settle
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Form.Item
          name="settlementType"
          label="Settlement Type"
          rules={[{ required: true, message: 'Settlement type is required' }]}
        >
          <Select
            placeholder="Select type"
            options={[
              { label: 'Credit Note', value: 'credit' },
              { label: 'Refund', value: 'refund' },
            ]}
          />
        </Form.Item>
        <Form.Item noStyle shouldUpdate={(prev, cur) => prev.settlementType !== cur.settlementType}>
          {({ getFieldValue }) => {
            const type = getFieldValue('settlementType');
            if (type === 'credit') {
              return (
                <Form.Item name="creditNoteRef" label="Credit Note Reference">
                  <Input placeholder="Credit note ref #" />
                </Form.Item>
              );
            }
            if (type === 'refund') {
              return (
                <>
                  <Form.Item name="refundRef" label="Refund Reference">
                    <Input placeholder="Refund ref #" />
                  </Form.Item>
                  <Form.Item name="refundAmount" label="Refund Amount">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      precision={2}
                      prefix={BASE_CURRENCY_CODE}
                      placeholder="Amount"
                    />
                  </Form.Item>
                </>
              );
            }
            return null;
          }}
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Main Page ──

export function ReturnsListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<PurchReturn | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [settleOpen, setSettleOpen] = useState(false);

  const handleFetch = useCallback(async (params: FetchParams) => {
    return fetchReturns(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const openDetail = async (id: number) => {
    setDrawerOpen(true);
    setDetailLoading(true);
    try {
      const data = await getReturn(id);
      setDetail(data);
    } catch {
      message.error('Failed to load return details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!detail) return;
    try {
      const updated = await approveReturn(detail.id);
      setDetail(updated);
      message.success('Return approved');
      refresh();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'response' in err
        ? (err as { response: { data: { error: string } } }).response?.data?.error
        : 'Failed to approve';
      message.error(msg || 'Failed to approve');
    }
  };

  const drawerActions = [];
  if (detail?.status === 'pending') {
    drawerActions.push({
      key: 'approve',
      label: 'Approve Return',
      icon: <CheckOutlined />,
      onClick: handleApprove,
    });
  }
  if (detail?.status === 'approved') {
    drawerActions.push({
      key: 'settle',
      label: 'Settle Return',
      icon: <DollarOutlined />,
      onClick: () => setSettleOpen(true),
    });
  }

  const statusCfg = detail ? RETURN_STATUS_CONFIG[detail.status] || { label: detail.status, color: 'default' } : undefined;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Returns</Title>
      </Flex>

      <DataTable<PurchReturn>
        columns={columns}
        fetchData={handleFetch}
        rowKey="id"
        toolbar={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            New Return
          </Button>
        }
        onRow={(record) => ({
          onClick: () => openDetail(record.id),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 1100 }}
      />

      <CreateReturnModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={refresh}
      />

      <SettleReturnModal
        open={settleOpen}
        returnRecord={detail}
        onClose={() => setSettleOpen(false)}
        onSettled={() => {
          if (detail) openDetail(detail.id);
          refresh();
        }}
      />

      <DetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDetail(null); }}
        title={detail?.returnCode || ''}
        subtitle={detail?.grnLine?.grnHeader?.grnCode ? `GRN: ${detail.grnLine.grnHeader.grnCode}` : undefined}
        status={statusCfg}
        loading={detailLoading}
        actions={drawerActions}
        width={700}
      >
        {detail && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Return Code">{detail.returnCode}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={statusCfg?.color}>{statusCfg?.label}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="GRN">
              {detail.grnLine?.grnHeader?.grnCode || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="GRN Line ID">{detail.grnLineId}</Descriptions.Item>
            <Descriptions.Item label="Return Qty">{detail.returnQty}</Descriptions.Item>
            <Descriptions.Item label="Return Value">
              {BASE_CURRENCY_CODE} {Number(detail.returnValue ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </Descriptions.Item>
            <Descriptions.Item label="Return Date">
              {detail.returnDate ? dayjs(detail.returnDate).format(DISPLAY_DATE_FORMAT) : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Reason Code">
              {detail.reasonCode || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Return Reason" span={2}>
              {detail.returnReason}
            </Descriptions.Item>
            {detail.settlementType && (
              <>
                <Descriptions.Item label="Settlement Type">
                  <Tag color={detail.settlementType === 'credit' ? 'blue' : 'green'}>
                    {detail.settlementType === 'credit' ? 'Credit Note' : 'Refund'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Reference">
                  {detail.creditNoteRef || detail.refundRef || '—'}
                </Descriptions.Item>
                {detail.refundAmount != null && (
                  <Descriptions.Item label="Refund Amount">
                    {BASE_CURRENCY_CODE} {Number(detail.refundAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </Descriptions.Item>
                )}
              </>
            )}
            {detail.carrierName && (
              <Descriptions.Item label="Carrier">{detail.carrierName}</Descriptions.Item>
            )}
            {detail.trackingNumber && (
              <Descriptions.Item label="Tracking">{detail.trackingNumber}</Descriptions.Item>
            )}
          </Descriptions>
        )}
      </DetailDrawer>
    </div>
  );
}
